import i18n from 'i18n';
import type { User, UserId } from 'features/users/types';
import { VideoPipSettings } from 'utils/pip/video-pip/VideoPipManager';
import { DrawOptions, PipRenderer } from 'utils/pip/PipRendererer';
import { capitalize } from 'utils/text';
import { truncateText } from 'utils/canvas';
import { isVideoReady } from 'utils/video';

const MIC_GLYPH = '\ue942';
const MIC_MUTED_GLYPH = '\ue901';
const HAND_RAISED_GLYPH = '\ue9a0';

export const LOCAL_VIDEO_PIP_ID = 'LOCAL_VIDEO_STREAM';
export const REMOTE_VIDEO_PIP_ID = (userId: UserId) => `REMOTE_VIDEO_${userId}`;

export const DEFAULT_VIDEO_PIP_SETTINGS: VideoPipSettings = {
  showPlaceholder: false,
  active: false,
  muted: false,
};

export class VideoPipRenderer implements PipRenderer<VideoPipSettings> {
  private ctx: CanvasRenderingContext2D | null = null;

  private settings: VideoPipSettings = DEFAULT_VIDEO_PIP_SETTINGS;

  private user: User | null = null;

  init(options: DrawOptions<VideoPipSettings>) {
    this.settings = options.settings || this.settings;
    this.ctx = options.ctx || this.ctx;
    this.user = options.user || this.user;
  }

  reset() {
    this.settings = DEFAULT_VIDEO_PIP_SETTINGS;
    this.ctx = null;
    this.user = null;
  }

  draw(): number {
    if (!this.ctx || !this.user) {
      return requestAnimationFrame(() => this.draw());
    }

    if (this.settings.showPlaceholder) {
      this.drawPlaceholder();

      return requestAnimationFrame(() => this.draw());
    }

    const videoElement = this.getVideoElement();
    if (videoElement && isVideoReady(videoElement)) {
      this.drawVideo(videoElement);
    } else {
      this.drawAvatar();
    }

    return requestAnimationFrame(() => this.draw());
  }

  private clearCanvas() {
    if (!this.ctx) {
      return;
    }

    this.ctx.reset();
    this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

    this.ctx.fillStyle = '#2C2E33';
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
  }

  private drawPlaceholder() {
    if (!this.ctx) {
      return;
    }

    this.clearCanvas();

    this.ctx.font = `18px Arial`;
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(
      i18n.t('room:grid.no_tiles_placeholder'),
      this.ctx.canvas.width / 2,
      this.ctx.canvas.height / 2
    );
  }

  private drawAvatar() {
    if (!this.ctx || !this.user) {
      return;
    }

    this.clearCanvas();

    const radius = Math.min(this.ctx.canvas.width, this.ctx.canvas.height) * 0.2;
    const x = this.ctx.canvas.width / 2;
    const y = this.ctx.canvas.height / 2;

    this.ctx.beginPath();
    this.ctx.arc(x, y - 5, radius, 0, Math.PI * 2);
    this.ctx.fillStyle = this.user.avatarColor;
    this.ctx.fill();

    const fullName = this.user.name ?? '';
    const formattedName = fullName
      .trim()
      .split(' ')
      .slice(0, 2)
      .map((word: string) => word[0]?.toUpperCase() ?? '')
      .join('');

    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.font = `${radius * 0.9}px Arial`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(formattedName, x, y);

    this.drawNameBadge();
    this.drawMicIcon();
    this.drawHandRaisedIcon();
  }

  private drawVideo(video: HTMLVideoElement) {
    if (!this.ctx) {
      return;
    }

    const { width, height } = this.ctx.canvas;

    this.ctx.reset();
    this.ctx.clearRect(0, 0, width, height);

    const scale = Math.max(width / video.videoWidth, height / video.videoHeight);
    const drawW = video.videoWidth * scale;
    const drawH = video.videoHeight * scale;
    const x = (width - drawW) / 2;
    const y = (height - drawH) / 2;

    this.ctx.drawImage(video, x, y, drawW, drawH);

    this.drawHandRaisedIcon();
    this.drawNameBadge();
    this.drawMicIcon();
  }

  private drawNameBadge() {
    if (!this.ctx || !this.user) {
      return;
    }

    const fullName = this.user.local ? capitalize(i18n.t('common:you')) : (this.user.name ?? '');

    const nameContainerPaddingX = 20;
    const containerHeight = 36;
    const borderRadius = 20;
    const fontSize = 16;

    this.ctx.font = `${fontSize}px Arial`;

    const maxTextWidth = this.ctx.canvas.width * 0.8;
    const slicedName = truncateText(this.ctx, fullName, maxTextWidth);
    const textWidth = this.ctx.measureText(slicedName).width;

    const containerWidth = Math.max(textWidth + nameContainerPaddingX * 2, 60);

    const badgeX = this.ctx.canvas.width / 2 - containerWidth / 2;
    const badgeY = 10;

    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    this.ctx.beginPath();
    this.ctx.roundRect(badgeX, badgeY, containerWidth, containerHeight, borderRadius);
    this.ctx.fill();

    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(
      slicedName,
      this.ctx.canvas.width / 2,
      badgeY + containerHeight / 2 + (containerHeight / 2 - fontSize)
    );
  }

  private drawMicIcon() {
    if (!this.ctx) {
      return;
    }

    const sizePx = 32;
    const padding = 12;
    const bgPadding = 6;
    const glyph = this.settings.muted ? MIC_MUTED_GLYPH : MIC_GLYPH;
    const totalSize = sizePx + bgPadding * 2;
    const x = padding;
    const y = this.ctx.canvas.height - padding - totalSize;

    this.ctx.save();
    this.ctx.beginPath();
    const r = totalSize / 2;
    this.ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    this.ctx.fill();
    this.ctx.restore();

    this.ctx.save();
    this.ctx.fillStyle = this.settings.active && !this.settings.muted ? '#00ff00' : '#FFFFFF';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.font = `${sizePx}px icomoon, icomoon-fallback`;
    this.ctx.fillText(glyph, x + totalSize / 2, y + totalSize / 2);
    this.ctx.restore();
  }

  private drawHandRaisedIcon() {
    if (!this.ctx || !this.user) {
      return;
    }

    if (!this.user.handRaised) {
      return;
    }

    const sizePx = 32;
    const padding = 12;
    const bgPadding = 6;
    const totalSize = sizePx + bgPadding * 2;
    const x = padding;
    const y = padding;

    this.ctx.save();
    this.ctx.beginPath();
    const r = totalSize / 2;
    this.ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.fill();
    this.ctx.restore();

    this.ctx.save();
    this.ctx.fillStyle = '#000000';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.font = `${sizePx}px icomoon, icomoon-fallback`;
    this.ctx.fillText(HAND_RAISED_GLYPH, x + totalSize / 2, y + totalSize / 2);
    this.ctx.restore();
  }

  private getVideoElement(): HTMLVideoElement | null {
    if (!this.user) {
      return null;
    }

    const element = document.getElementById(
      this.user.local ? LOCAL_VIDEO_PIP_ID : REMOTE_VIDEO_PIP_ID(this.user.id)
    );

    if (element) {
      return element as HTMLVideoElement;
    }

    return null;
  }
}
